"use client";

import Contact from "@/components/contact";
import ServicesList from "@/components/ServicesList";
import Testimonials from "@/components/testimonials";
import { Button } from "@/components/ui/button";
import { useUser } from "@/context/user-context";
import {
  caseStudies,
  our_customers,
  services,
  teamMembers,
} from "@/data/dummy";
// import { EmailFooter } from "@/emails/EmailFooter";
// import { EmailHeader } from "@/emails/EmailHeader";
// import EmailLayout from "@/emails/EmailLayout";
import { cn } from "@/lib/utils";
import Link from "next/link";

export default function Home() {
  const { isAuthenticated } = useUser();
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-linear-to-br from-white via-slate-50 to-cyan-50/60 text-gray-900">
        <div className="absolute inset-x-0 bottom-0 h-px bg-linear-to-r from-transparent via-cyan-200 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid lg:grid-cols-[1.08fr_0.92fr] gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="relative overflow-hidden border border-cyan-100/80 bg-white/85 p-6 sm:p-8 shadow-sm">
              <div className="absolute inset-y-0 right-0 w-full sm:w-[58%]">
                <img
                  src="/images/hexad.jpg"
                  alt="Hexad project visual"
                  className="h-full w-full object-cover opacity-[0.18]"
                />
                <div className="absolute inset-0 bg-linear-to-r from-white via-white/85 to-white/25" />
              </div>
              <div className="absolute -bottom-24 -right-20 h-64 w-64 border border-cyan-200/70" />
              <div className="relative max-w-xl space-y-8">
                <div>
                  <p className="text-cyan-700 text-xs uppercase font-semibold tracking-[0.18em] mb-4">
                    Our Expertise
                  </p>
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-normal">
                    Transforming Industries Through
                    <span className="text-cyan-700"> Innovation</span> and
                    <span className="text-cyan-700"> Excellence</span>
                  </h1>
                  <p className="mt-6 max-w-lg text-gray-600 leading-relaxed">
                    Hexad partners with organizations across technology,
                    engineering, construction, and business transformation to
                    deliver practical solutions with measurable impact.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    href={isAuthenticated ? "/request-quote" : "/auth/login"}
                  >
                    <Button
                      size="lg"
                      className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-2 shadow-sm shadow-cyan-900/10"
                    >
                      Get Started
                    </Button>
                  </Link>
                  <Link href="/about">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border border-cyan-500 bg-white/80 text-gray-900 hover:bg-cyan-900 hover:text-white px-6 py-2"
                    >
                      Learn More
                    </Button>
                  </Link>
                </div>
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 sm:gap-6 pt-6 border-t border-gray-200">
                  <div>
                    <div className="text-xl font-bold text-cyan-700">7+</div>
                    <div className="text-sm text-gray-600">
                      Projects Completed
                    </div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-cyan-700">5+</div>
                    <div className="text-sm text-gray-600">
                      Years Experience
                    </div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-cyan-700">98%</div>
                    <div className="text-sm text-gray-600">
                      Client Satisfaction
                    </div>
                  </div>
                </div>
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-gray-500">
                  Smart planning. Strong execution. Sustainable results.
                </p>
              </div>
            </div>

            {/* Right Content - Compact Services Overview */}
            <ServicesList services={services} />
          </div>
        </div>
      </section>

      {/* Our Aproach Section */}
      <section className="relative top-17 flex flex-col lg:flex-row items-center gap-6 lg:gap-8 h-auto lg:h-[550px] z-10 w-[90%] lg:w-[80%] mx-auto mb-72">
        {/* Image Card */}
        <div className="w-[90%] ml-auto lg:w-1/2 h-[600px] lg:h-[700px] relative overflow-hidden group hover-scale transition-all duration-500">
          <img
            src="https://i.pinimg.com/1200x/4d/de/02/4dde02825527c83bd74bc5d5d49ba5ac.jpg"
            // src="/bg-site.png"
            alt="Hexad Consulting Solutions"
            className="w-full sm:w-[70%] lg:w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-linear-to-r from-white to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="absolute bottom-6 left-6 text-cyan-900 opacity-40 group-hover:opacity-100 transition-opacity duration-500 delay-200">
            <p className="text-lg font-semibold">Transforming Businesses</p>
            <p className="text-sm opacity-90">Since 2021</p>
          </div>
        </div>

        {/* Text Content Card - Comes First on Mobile */}
        <div className="w-full lg:w-1/2 h-full bg-white p-8 lg:p-12 flex flex-col justify-center border border-gray-100 relative">
          <div className="relative space-y-8">
            <div>
              <span className="inline-block px-3 py-1 bg-cyan-100 text-cyan-700  text-xs uppercase font-medium mb-4">
                Our Approach
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                Innovative Solutions for Complex Challenges
              </h2>
            </div>

            <p className="text-gray-600 leading-relaxed">
              At Hexad, we believe in partnership-driven consulting. We work
              closely with our clients to understand their unique challenges and
              deliver tailored solutions that create lasting impact and drive
              measurable results.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
              <div className="flex items-center space-x-3 p-3  hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 bg-cyan-700  flex items-center justify-center">
                  <span className="text-cyan-100 font-bold text-sm">✓</span>
                </div>
                <span className="text-gray-700 font-medium">
                  Quality Assured
                </span>
              </div>
              <div className="flex items-center space-x-3 p-3  hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 bg-cyan-700  flex items-center justify-center">
                  <span className="text-cyan-100 font-bold text-sm">✓</span>
                </div>
                <span className="text-gray-700 font-medium">
                  Timely Delivery
                </span>
              </div>
              <div className="flex items-center space-x-3 p-3  hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 bg-cyan-700  flex items-center justify-center">
                  <span className="text-cyan-100 font-bold text-sm">✓</span>
                </div>
                <span className="text-gray-700 font-medium">Expert Team</span>
              </div>
              <div className="flex items-center space-x-3 p-3  hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 bg-cyan-700  flex items-center justify-center">
                  <span className="text-cyan-100 font-bold text-sm">✓</span>
                </div>
                <span className="text-gray-700 font-medium">24/7 Support</span>
              </div>
            </div>

            {/* Clients Section */}
            <div className="pt-8 border-t border-gray-200">
              <div className="flex items-center space-x-4">
                <div className="flex -space-x-3">
                  {our_customers.map((customer, index) => (
                    <img
                      key={index}
                      src={customer.image}
                      alt={customer.name}
                      className="w-8 h-8 rounded-full border-2 border-[rgb(0,146,184)] object-cover"
                    />
                  ))}
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    Trusted by Industry Leaders
                  </div>
                  <div className="text-xs text-gray-600">
                    Join 100+ satisfied clients worldwide
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link href={isAuthenticated ? "/request-quote" : "/auth/login"}>
                <Button
                  size="lg"
                  className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-2 transition-colors duration-300"
                >
                  Get Started Today
                </Button>
              </Link>
              <Link href="/bronchures">
                <Button
                  size="lg"
                  variant="outline"
                  className="border border-cyan-500 text-gray-900 hover:bg-cyan-900 hover:text-white font-semibold px-6 py-2 transition-colors duration-300"
                >
                  Download Brochures
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="relative -top-47 lg:top-0 py-8 lg:py-12 px-4 sm:px-6 lg:px-8 xl:px-40 mb-16 lg:mb-20">
        {/* Header */}
        <div className="mb-12 lg:mb-16 text-center">
          <p className="text-cyan-600 text-xs uppercase font-medium tracking-wider mb-3">
            Our Services
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-4">
            Comprehensive Solutions for Your Business
          </h2>
          <p className="text-gray-600 text-sm sm:text-base">
            We deliver innovative services tailored to drive your business
            growth and transformation
          </p>
        </div>

        {/* Services Grid - Better responsive control */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 justify-items-center max-w-5xl mx-auto">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-white overflow-hidden border border-gray-100 transition-all duration-300 w-[80%]"
            >
              <div
                className={cn(
                  "h-72 overflow-hidden",
                  index === 1 && "mt-20",
                  index === 3 && "mt-20",
                )}
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:translate-x-2 transition-transform duration-300"
                />
              </div>
              <div className="relative p-6 -mt-10 z-20 bg-white ml-10 group-hover:-translate-x-4 transition-all duration-500">
                <h3 className="h3">{service.title}</h3>
                <p className="paragraph">{service.description}</p>
                <Link href="/services/9">
                  <Button
                    variant={"ghost"}
                    className="text-cyan-600 hover:text-cyan-700 transition-colors duration-300 flex items-center gap-2 group/btn px-0"
                  >
                    Learn More
                    <span className="group-hover/btn:translate-x-1 transition-transform duration-300">
                      →
                    </span>
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-12 lg:mt-16">
          <Link href="/services">
            <Button
              size="lg"
              className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-8 py-3 transition-colors duration-300"
            >
              View All Services
            </Button>
          </Link>
        </div>
      </section>

      {/* Testimonies */}
      <Testimonials />

      {/* Team Section */}
      <section className="pb-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-cyan-600 text-xs uppercase font-medium mb-3">
              Our Team
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Meet The Experts
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Our diverse team of seasoned professionals brings decades of
              combined experience in technology, engineering, and business
              transformation.
            </p>
          </div>

          {/* Team member cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 lg:mx-2">
            {teamMembers.slice(0, 3).map((member, index) => (
              <div
                key={index}
                className="bg-white shadow-sm overflow-hidden border-l-4 border-cyan-700 hover:shadow-md transition-all duration-300 group hover:-translate-y-3.5"
              >
                <div className="h-80 overflow-hidden relative p-5">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-contain transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {member.name}
                  </h3>
                  <p className="text-cyan-600 font-semibold mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {member.bio}
                  </p>
                  <div className="flex space-x-3">
                    {member.socials.linkedin && (
                      <button className="text-gray-400 hover:text-cyan-600 transition-colors duration-300">
                        <span className="sr-only">LinkedIn</span>
                        {/* LinkedIn Icon */}
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761
                2.239 5 5 5h14c2.762 0 5-2.239
                5-5v-14c0-2.761-2.238-5-5-5zm-11
                19h-3v-11h3v11zm-1.5-12.268c-.966
                0-1.75-.79-1.75-1.764s.784-1.764
                1.75-1.764 1.75.79 1.75
                1.764-.783 1.764-1.75
                1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4
                0v5.604h-3v-11h3v1.765c1.396-2.586
                7-2.777 7 2.476v6.759z"
                          />
                        </svg>
                      </button>
                    )}
                    {member.socials.twitter && (
                      <button className="text-gray-400 hover:text-cyan-600 transition-colors duration-300">
                        <span className="sr-only">Twitter</span>
                        {/* Twitter Icon */}
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M23.953 4.57a10 10 0 01-2.825.775
                4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127
                1.184a4.92 4.92 0 00-8.384
                4.482C7.69 8.095 4.067 6.13 1.64
                3.162a4.822 4.822 0 00-.666
                2.475c0 1.71.87 3.213 2.188
                4.096a4.904 4.904 0 01-2.228-.616v.06a4.923
                4.923 0 003.946 4.827 4.996
                4.996 0 01-2.212.085 4.936
                4.936 0 004.604 3.417 9.867
                9.867 0 01-6.102 2.105c-.39
                0-.779-.023-1.17-.067a13.995
                13.995 0 007.557 2.209c9.053
                0 13.998-7.496 13.998-13.985
                0-.21 0-.42-.015-.63A9.935
                9.935 0 0024 4.59z"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-10">
            <Link href="/team">
              <Button
                size="lg"
                variant={"outline"}
                className="text-cyan-600 hover:text-cyan-700 font-semibold px-12 py-3 transition-colors duration-300"
              >
                View All Team
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <Contact />

      {/* Case Studies Section - Stats Focused */}
      <section className="mb-10 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-5 -mt-32 pt-10 pb-4">
            <p className="text-cyan-600 text-xs uppercase font-medium mb-3">
              Case Studies
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Proven Results
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              See how we've helped businesses achieve remarkable outcomes
              through innovative solutions
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Featured Case Study */}
            <div className="bg-white shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300 p-7">
              <div className="h-64 overflow-hidden relative">
                <img
                  src="https://i.pinimg.com/736x/68/5e/8a/685e8ab1c0eab0735a4c1bc951116e66.jpg"
                  alt="Featured Case Study"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-6 text-white">
                  <span className="bg-cyan-600 text-white px-3 py-1 text-xs font-medium mb-2 inline-block">
                    Featured
                  </span>
                  <h3 className="text-xl font-bold">
                    Global Retail Transformation
                  </h3>
                </div>
              </div>

              <div>
                <p className="text-gray-600 text-sm mb-4 my-5">
                  Digital transformation across 12 countries resulting in
                  unprecedented growth and operational excellence.
                </p>
                <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">
                  Read Full Case Study
                </Button>
              </div>
            </div>

            {/* Smaller Case Studies */}
            <div className="space-y-6">
              {caseStudies.map((study, index) => (
                <div
                  key={index}
                  className="bg-white p-6 shadow-md hover:shadow-lg transition-all duration-300 group cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-bold text-gray-900 group-hover:text-cyan-600 transition-colors">
                      {study.title}
                    </h4>
                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-medium">
                      {study.category}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span className="font-semibold text-cyan-600">
                      {study.result}
                    </span>
                    <span>{study.duration}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// export default function Home() {
//   const name = "John Doe";
//   const topic = "Project Kickoff Meeting";
//   const date = new Date("2024-07-15T10:00:00Z");
//   const formattedDate = date.toLocaleString("en-US", {
//     weekday: "long",
//     year: "numeric",
//     month: "long",
//     day: "numeric",
//     hour: "2-digit",
//     minute: "2-digit",
//     timeZoneName: "short",
//   });
//   const duration = 60;
//   const meetingType = "Video Conference";
//   const notes = "Please prepare the project requirements document.";
//   const meetingLink = "https://meet.hexad.co.rw/meeting/123456";
//   const timezone = "UTC";

//   const participants = [
//     {
//       id: 29,
//       consultationId: "6dda2e72-316b-4347-b1f2-c87c91855d09",
//       email: "happy222004567@gmail.com",
//       createdAt: "Mon Jan 12 2026 09:49:08 GMT+0200 (Central Africa Time)",
//     },
//     {
//       id: 30,
//       consultationId: "6dda2e72-316b-4347-b1f2-c87c91855d09",
//       email: "jhappy.manyenzi@hexad.co.rw",
//       createdAt: "Mon Jan 12 2026 09:49:08 GMT+0200 (Central Africa Time)",
//     },
//     {
//       id: 31,
//       consultationId: "6dda2e72-316b-4347-b1f2-c87c91855d09",
//       email: "coroute.canada@gmail.com",
//       createdAt: "Mon Jan 12 2026 09:49:08 GMT+0200 (Central Africa Time)",
//     },
//   ];

//   return (
//     <EmailLayout>
//       <div>
//         <EmailHeader
//           logoUrl="https://res.cloudinary.com/dsqqgod3p/image/upload/v1768039275/logo-logo_z9hvee.jpg"
//           companyName="Hexad Ltd"
//           address="KG 14 Ave, Kigali, Rwanda"
//           subtitle="Head office"
//         />
//         <div style={{ marginBottom: "35px" }}>
//           <h1
//             style={{
//               color: "#075985",
//               fontSize: "28px",
//               fontWeight: "600",
//               margin: "0 0 15px 0",
//               lineHeight: "1.3",
//             }}
//           >
//             Meeting Confirmed
//           </h1>
//           <div style={{ color: "#555", fontSize: "16px" }}>
//             <p style={{ margin: "0 0 20px 0" }}>
//               Hello {name}, your meeting with the Hexad team has been
//               successfully scheduled. Please find the details below.
//             </p>
//           </div>
//         </div>

//         <div
//           style={{
//             border: "1px solid #e5e7eb",
//             borderRadius: "4px",
//             padding: "20px",
//             backgroundColor: "#f9fafb",
//             marginBottom: "25px",
//           }}
//         >
//           <h3
//             style={{
//               marginTop: 0,
//               marginBottom: "15px",
//               fontSize: "18px",
//               fontWeight: "600",
//               color: "#075985",
//               borderBottom: "2px solid #e5e7eb",
//               paddingBottom: "10px",
//             }}
//           >
//             Meeting Details
//           </h3>

//           <table
//             width="100%"
//             cellPadding="0"
//             cellSpacing="0"
//             style={{
//               borderCollapse: "collapse",
//               fontSize: "14px",
//               backgroundColor: "#ffffff",
//             }}
//           >
//             <tbody>
//               {/* Topic */}
//               <tr>
//                 <td
//                   style={{
//                     padding: "10px",
//                     fontWeight: 600,
//                     color: "#374151",
//                     width: "35%",
//                     backgroundColor: "#f9fafb",
//                   }}
//                 >
//                   Topic
//                 </td>
//                 <td style={{ padding: "10px", color: "#111827" }}>{topic}</td>
//               </tr>

//               {/* Date & Time */}
//               <tr>
//                 <td
//                   style={{
//                     padding: "10px",
//                     fontWeight: 600,
//                     color: "#374151",
//                     backgroundColor: "#f9fafb",
//                   }}
//                 >
//                   Date & Time
//                 </td>
//                 <td style={{ padding: "10px", color: "#111827" }}>
//                   {formattedDate} ({timezone})
//                 </td>
//               </tr>

//               {/* Duration */}
//               <tr>
//                 <td
//                   style={{
//                     padding: "10px",
//                     fontWeight: 600,
//                     color: "#374151",
//                     backgroundColor: "#f9fafb",
//                   }}
//                 >
//                   Duration
//                 </td>
//                 <td style={{ padding: "10px", color: "#111827" }}>
//                   {duration} minutes
//                 </td>
//               </tr>

//               {/* Type */}
//               <tr>
//                 <td
//                   style={{
//                     padding: "10px",
//                     fontWeight: 600,
//                     color: "#374151",
//                     backgroundColor: "#f9fafb",
//                   }}
//                 >
//                   Type
//                 </td>
//                 <td style={{ padding: "10px" }}>
//                   <span
//                     style={{
//                       display: "inline-block",
//                       backgroundColor: "#e0f2fe",
//                       color: "#075985",
//                       padding: "4px 10px",
//                       borderRadius: "12px",
//                       fontWeight: 600,
//                       fontSize: "12px",
//                     }}
//                   >
//                     {meetingType}
//                   </span>
//                 </td>
//               </tr>

//               {/* Notes (optional) */}
//               {notes && (
//                 <tr>
//                   <td
//                     style={{
//                       padding: "10px",
//                       fontWeight: 600,
//                       color: "#374151",
//                       backgroundColor: "#f9fafb",
//                       verticalAlign: "top",
//                     }}
//                   >
//                     Notes
//                   </td>
//                   <td
//                     style={{
//                       padding: "10px",
//                       color: "#374151",
//                       lineHeight: "1.5",
//                     }}
//                   >
//                     {notes}
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>

//           {participants.length > 0 && (
//             <div
//               style={{
//                 marginTop: "20px",
//               }}
//             >
//               <h3
//                 style={{
//                   marginTop: 0,
//                   marginBottom: "15px",
//                   fontSize: "18px",
//                   fontWeight: "600",
//                   borderBottom: "1px solid #e5e7eb",
//                   paddingBottom: "10px",
//                 }}
//               >
//                 Participants ({participants.length})
//               </h3>

//               <table
//                 width="100%"
//                 cellPadding="0"
//                 cellSpacing="0"
//                 style={{ borderCollapse: "collapse", fontSize: "14px" }}
//               >
//                 <tbody>
//                   {participants.map((participant, index) => (
//                     <tr key={participant.id}>
//                       <td
//                         style={{
//                           padding: "8px 0",
//                           color: "#075985",
//                           fontWeight: 500,
//                         }}
//                       >
//                         {index + 1}.
//                       </td>
//                       <td style={{ padding: "8px 0", color: "#333" }}>
//                         {participant.email}
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}

//           {meetingLink && (
//             <div style={{ marginTop: "20px", textAlign: "center" }}>
//               <a
//                 href={meetingLink}
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 style={{
//                   display: "inline-block",
//                   backgroundColor: "#06b6d4",
//                   color: "white",
//                   padding: "12px 24px",
//                   textDecoration: "none",
//                   borderRadius: "4px",
//                   fontWeight: "bold",
//                 }}
//               >
//                 Join Meeting
//               </a>
//             </div>
//           )}
//         </div>

//         <p>
//           If you need to reschedule or have any questions, please contact us at{" "}
//           <a
//             href="mailto:info@hexad.co.rw"
//             style={{ color: "#0891b2", textDecoration: "none" }}
//           >
//             info@hexad.co.rw
//           </a>
//           .
//         </p>

//         <EmailFooter />
//       </div>
//     </EmailLayout>
//   );
// }
